import React, { useState, useMemo } from 'react';
import { Dumbbell } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, GenderField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateFFMI, getFFMICategory } from '../../lib/calculatorUtils';

const FFMICalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [bf, setBF] = useState('');
  const [gender, setGender] = useState('male');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    const b = parseFloat(bf);
    if (!h || !w || !b || b <= 0 || b >= 60) return null;
    const r = calculateFFMI(w, h, b);
    return { ...r, category: getFFMICategory(r.adjusted, gender) };
  }, [heightCm, weightKg, bf, gender]);

  const inputs = (
    <div className="space-y-5" data-testid="ffmi-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Composition</h2>
      <p className="text-slate-400 text-sm mb-5">FFMI compares muscle mass relative to your height.</p>
      <GenderField value={gender} onChange={setGender} testIdPrefix="ffmi-gender" />
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <NumberField id="ffmi-bf" label="Body Fat %" value={bf} onChange={setBF} placeholder="e.g. 15" suffix="%" min={2} max={60} testId="ffmi-bf-input" required />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Adjusted FFMI" value={result.adjusted} accent="from-orange-500 to-rose-500" category={result.category} testId="ffmi-result-adjusted" />
      <div className="mt-8 space-y-1">
        <StatRow label="Raw FFMI" value={result.ffmi} testId="ffmi-raw" />
        <StatRow label="Lean Body Mass" value={result.lbm} unit="kg" color="text-emerald-300" testId="ffmi-lbm" />
        <StatRow label="Category" value={result.category.label} color={result.category.color} testId="ffmi-category" />
        <StatRow label="Natural ceiling (typical)" value={gender === 'male' ? '~ 25' : '~ 22'} testId="ffmi-ceiling" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Adjusted FFMI normalizes to a 1.8 m height. Values above the natural ceiling are uncommon in drug-free individuals.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter height, weight and body fat % to see your FFMI." />
  );

  return (
    <CalculatorLayout
      slug="ffmi" title="FFMI Calculator"
      subtitle="Fat-Free Mass Index, an advanced measure of muscle development relative to your height."
      metaDescription="FFMI Calculator from TCUBE FITNESS with Fat-Free Mass Index and adjusted FFMI for muscle assessment."
      icon={Dumbbell}
      accentGradient="from-orange-500 to-rose-500" accentColor="text-orange-400"
      badgeBg="bg-orange-500/10" badgeBorder="border-orange-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Formula:</strong> FFMI = LBM (kg) ÷ Height² (m)</p>
        <p>Adjusted FFMI = FFMI + 6.1 × (1.8 − height in m), normalizing to a 1.8 m reference height.</p>
      </>}
    />
  );
};

export default FFMICalculatorPage;
